import { useNavigateBackToContentStudio } from '@src/hooks/useNavigateBackToContentStudio';
import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import React from 'react';
import ReactDOM from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { COMPACT_TOOLTIP_STYLE } from '../common/tooltipStyles';
import { VerticalSeparator } from '../common/VerticalSeparator';

/**
 * "Back to Content Studio" affordance in the Creator Studio toolbar. Always an
 * explicit deep link (see useNavigateBackToContentStudio): back to the edited
 * clip's preview modal when it resolves, the story's clip list otherwise.
 * Styled after the What's New drawer's back button (WhatsNewDrawer.tsx); the
 * trailing rule is the shared VerticalSeparator.
 */
export const BackToContentStudioButton: React.FC = observer(() => {
  const userIdentity = useUserIdentity();
  const { enableCreatorStudioNav } = useFlagsCombination(
    userIdentity.currentRole,
  );
  const { navigateBack, saving } = useNavigateBackToContentStudio();
  // PLAT-11929 — the last edits are being saved before leaving.
  const label = saving ? 'Saving…' : 'Back to Content Studio';

  if (!enableCreatorStudioNav) return null;

  return (
    <>
      <Main
        onClick={navigateBack}
        disabled={saving}
        aria-busy={saving || undefined}
        aria-label={label}
        data-testid="creator-back-to-content-studio"
        data-tooltip-id="back-to-content-studio-tooltip"
        data-tooltip-content={label}
      >
        &larr;
      </Main>
      <VerticalSeparator />
      {/* Portaled: the toolbar's stacking context (z 100002) paints below the
          sidebar rail (z 1000002), so an inline tooltip is clipped by it. */}
      {ReactDOM.createPortal(
        <Tooltip
          id="back-to-content-studio-tooltip"
          style={COMPACT_TOOLTIP_STYLE}
          place="bottom"
          positionStrategy="fixed"
        />,
        document.body,
      )}
    </>
  );
});

/**
 * Sized to sit inside the left pane's margin — the strip left of the transcript's
 * content edge, which the trailing `Separator` is aligned to. `ToolbarCenter`
 * owns that arithmetic (28px would overflow the margin); the glyph stays centred
 * in whatever box is left, which is where it reads as being "in the margin".
 */
const Main = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 25px;
  height: 28px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  border-radius: var(--radius-input);
  font-size: 16px;
  transition: all var(--transition-fast) cubic-bezier(0.4, 0, 0.2, 1);

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    color: var(--foreground);
  }

  &:disabled {
    color: var(--text-muted);
    cursor: progress;
  }
`;
